/**
 * @module M/impl/control/ReferenceLocatorCatastroControl
 */
export default class ReferenceLocatorCatastroControl extends M.impl.Control {
  /**
   * This function adds the control to the specified map
   *
   * @public
   * @function
   * @param {M.Map} map to add the plugin
   * @param {HTMLElement} html of the plugin
   * @api stable
   */
  addTo(map, html) {
    /**
    * Facade map
    * @private
    * @type {M.map}
    */
    this.facadeMap_ = map;
    super.addTo(map, html);
  }

  readGeometry(source) {
    let newSource = source;
    let srs = this.facadeMap_.getProjection().code;
    if (newSource.indexOf('srsName="GCS_WGS_1984"') > -1) {
      newSource = newSource.replace(/srsName="GCS_WGS_1984"/gi, 'srsName="EPSG:4326"');
    }

    if (newSource.indexOf('cp:geometry') > -1) {
      newSource = newSource.replace(/cp:geometry/gi, 'ogr:geometryProperty');
    }

    if (newSource.indexOf('certificacion:the_geom') > -1) {
      newSource = newSource.replace(/certificacion:the_geom/gi, 'ogr:geometryProperty');
    }

    if (newSource.split('srsName="')[1].split('"')[0].indexOf('http') > -1) {
      try {
        srs = `EPSG:${newSource.split('srsName="')[1].split('#')[1].split('"')[0]}`;
      } catch (err) {
        srs = `EPSG:${newSource.split('srsName="')[1].split('/EPSG/')[1].split('/')[1]}`;
      }
    } else if (newSource.split('srsName="')[1].indexOf('crs:EPSG::') > -1) {
      srs = `EPSG:${newSource.split('srsName="')[1].split('::')[1].split('"')[0]}`;
    } else {
      srs = newSource.split('srsName="')[1].split('"')[0];
    }

    if (newSource.indexOf('<member>') > -1) {
      newSource = newSource.replace(/member/gi, 'gml:featureMember');
    } else if (newSource.indexOf('<wfs:member>') > -1) {
      newSource = newSource.replace(/wfs:member/gi, 'gml:featureMember');
    } else if (newSource.indexOf('<ogr:featureMember>') > -1) {
      newSource = newSource.replace(/ogr:featureMember/gi, 'gml:featureMember');
    }

    let features = new ol.format.WFS({ gmlFormat: new ol.format.GML2() }).readFeatures(newSource, {
      dataProjection: srs,
      featureProjection: srs,
    });

    features = features.map((f, index) => {
      const newF = f;
      if (!f.getGeometry()) {
        newF.setGeometry(f.get('geometry'));
      }

      return newF;
    });

    if (features.length === 0 || features[0].getGeometry() === undefined) {
      features = new ol.format.WFS({ gmlFormat: new ol.format.GML3() }).readFeatures(newSource, {
        dataProjection: srs,
        featureProjection: srs,
      });

      features = features.map((f, index) => {
        const newF = f;
        if (!f.getGeometry()) {
          newF.setGeometry(f.get('geometry'));
        }
        return newF;
      });
    }

    // En el caso de que no tenga geometrías, comprobamos si es GML 3.2,
    // si lo es tenemos que parsearlo a mano.
    if ((features.length === 0 || features[0].getGeometry() === undefined)
      && newSource.indexOf('gml/3.2') > 0) {
      features = this.gmlParser(newSource);
    }

    features = this.featuresToFacade(features);
    return features;
  }

  /**
  * Parse the features of a GML 3.2 layer
  * @private
  * @function
  * @param {*} source-
  */
  gmlParser(source) {
    const features = [];
    let superficies = this.parseSurfacesGml(source);
    if (superficies.length === 0) {
      superficies = this.parseGeometriesGml(source);
    }

    superficies = this.getEPSGFromGML32(source, superficies);
    const poligonos = this.getCoordinatesFromGML32(source, superficies);
    const geometriasPoligonos = [];
    poligonos.forEach((poligono) => {
      geometriasPoligonos.push({
        modo: poligono.modo,
        gml_tipo: poligono.tipo,
        gml: poligono.fichero,
        sistema: poligono.sistema,
        referencia: poligono.id,
        vertices: poligono.coordenadas.trim().split(' '),
      });
    });

    geometriasPoligonos.forEach((geometria) => {
      const polygonCoords = [];
      for (let i = 0; i < geometria.vertices.length; i += 2) {
        polygonCoords.push([parseFloat(geometria.vertices[i]), parseFloat(geometria.vertices[i + 1])]);
      }

      const newOlFeature = new ol.Feature({
        geometry: new ol.geom.Polygon([polygonCoords]),
      });

      newOlFeature.setId(`GML ${features.length + 1}`);
      features.push(newOlFeature);
    });

    return features;
  }

  /**
  * Parse the surfaces of a GML 3.2 layer
  * @private
  * @function
  * @param {*} source-
  */
  parseSurfacesGml(source) {
    const surfaces = [];
    const endTagSurface = '</gml:Surface>';
    let auxSource = source;
    while (auxSource !== '') {
      const firstSurfaceTag = auxSource.indexOf('<gml:Surface');
      let lastSurfaceTag = -2;
      if (firstSurfaceTag >= 0) {
        lastSurfaceTag = auxSource.indexOf(endTagSurface);
      }

      if (lastSurfaceTag > firstSurfaceTag) {
        surfaces.push({ surface: auxSource.substring(firstSurfaceTag, lastSurfaceTag + endTagSurface.length), id: '', sistema: '' });
        auxSource = auxSource.substring(lastSurfaceTag + endTagSurface.length, auxSource.length);
      } else if (auxSource.length !== 0) {
        auxSource = '';
      } else if (auxSource.length === 0) {
        break;
      }
    }

    return surfaces;
  }

  /**
  * Parse the other geometries of a GML 3.2 layer
  * @private
  * @function
  * @param {*} source-
  */
  parseGeometriesGml(source) {
    const geometries = [];
    const endTagPoint = '</gml:Point>';
    const endTagLine = '</gml:LineString>';
    const endTagInteriorPolygon = '</gml:interior>';
    const endTagPolygon = '</gml:exterior>';
    let polygonIndex = source.indexOf('<gml:exterior>');
    let lineIndex = source.indexOf('<gml:LineString');
    let pointIndex = source.indexOf('<gml:Point');
    let sourceAux = source;
    let indexInicial = -1;
    while (sourceAux !== '') {
      let indexEndTag = -2;
      if (polygonIndex !== '-1') {
        indexInicial = sourceAux.indexOf('<gml:exterior>');
        if (indexInicial >= 0) {
          indexEndTag = sourceAux.indexOf(endTagPolygon) + endTagPolygon.length;
        } else {
          polygonIndex = -1;
        }

        if (sourceAux.indexOf(endTagInteriorPolygon) >= 0) {
          indexEndTag = sourceAux.indexOf(endTagInteriorPolygon) + endTagInteriorPolygon.length;
        }
      }

      if (lineIndex !== -1 && indexInicial < 0) {
        indexInicial = sourceAux.indexOf('<gml:LineString');
        if (indexInicial >= 0) {
          indexEndTag = sourceAux.indexOf(endTagLine) + endTagLine.length;
        } else {
          lineIndex = -1;
        }
      }

      if (pointIndex !== -1 && indexInicial < 0) {
        indexInicial = sourceAux.indexOf('<gml:Point');
        if (indexInicial >= 0) {
          indexEndTag = sourceAux.indexOf(endTagPoint) + endTagPoint.length;
        } else {
          pointIndex = -1;
        }
      }

      if (indexInicial > indexEndTag) {
        geometries.push({ surface: sourceAux.substring(indexInicial, indexEndTag), id: '', sistema: '' });
      } else {
        sourceAux = '';
      }
    }

    return geometries;
  }

  /**
    * Parse the surface coordinates of a GML 3.2 layer
    * @private
    * @function
    * @param {*} source -
    * @param {*} superficies -
    */
  getCoordinatesFromGML32(source, superficies) {
    const poligonos = [];
    superficies.forEach((superficie) => {
      let exteriores = superficie.surface;
      let posiciones = superficie.surface;
      let contador = 0;
      while (exteriores !== '') {
        let indexInicial = exteriores.indexOf('gml:exterior');
        let indexFinal = -2;
        let coordenadas = '';
        if (indexInicial >= 0) {
          indexFinal = exteriores.indexOf('</gml:exterior');
          if (indexFinal > indexInicial) {
            const exteriorElement = exteriores.substring(indexInicial, indexFinal);
            const indexInicialElement = exteriorElement.indexOf('gml:posList');
            let indexFinalElement = -2;
            if (indexInicialElement >= 0) {
              indexFinalElement = exteriorElement.indexOf('</gml:posList>');
              if (indexFinalElement > indexInicialElement) {
                coordenadas = exteriorElement.substring(indexInicialElement, indexFinalElement);
              }
            }
            exteriores = exteriores.substring(indexFinal + 14, exteriores.length);
          } else {
            exteriores = '';
          }
        } else {
          exteriores = '';
        }

        if (coordenadas !== '') {
          indexInicial = coordenadas.indexOf('>');
          if (indexInicial >= 0) {
            coordenadas = coordenadas.substring(indexInicial + 1, coordenadas.legnth);
            const idSuperficie = superficie.id !== '' ? superficie.id : `ID_${contador}`;
            const nombreSuperficie = superficie.id !== '' ? superficie.id : `nombre_${contador}`;
            poligonos.push({
              modo: 'Exterior',
              fichero: undefined,
              tipo: undefined,
              id: `${idSuperficie}_${contador}`,
              coordenadas,
              nombre: `${nombreSuperficie}_${contador}`,
              sistema: superficie.sistema,
            });

            contador += 1;
          }
        }
      }

      while (posiciones.indexOf('<gml:posList>') !== -1) {
        const lineStringTag = '<gml:posList>';
        let coordenadas = '';
        const indexInicial = posiciones.indexOf(lineStringTag) + lineStringTag.length;
        const indexFinal = posiciones.indexOf('</gml:posList>');
        if (indexInicial >= 0) {
          coordenadas = posiciones.substring(indexInicial, indexFinal);
          posiciones = posiciones.substring(indexFinal, posiciones.length);
        }

        if (coordenadas !== '') {
          const idSuperficie = superficie.id !== '' ? superficie.id : `ID_${contador}`;
          const nombreSuperficie = superficie.id !== '' ? superficie.id : `nombre_${contador}`;
          poligonos.push({
            modo: 'Exterior',
            fichero: undefined,
            tipo: undefined,
            id: `${idSuperficie}_${contador}`,
            coordenadas,
            nombre: `${nombreSuperficie}_${contador}`,
            sistema: superficie.sistema,
          });

          contador += 1;
        }
      }

      while (posiciones.indexOf('<gml:pos>') !== -1) {
        const pointTag = '<gml:pos>';
        let coordenadas = '';
        const indexInicial = posiciones.indexOf(pointTag) + pointTag.length;
        const indexFinal = posiciones.indexOf('</gml:pos>');
        if (indexInicial >= 0) {
          coordenadas = posiciones.substring(indexInicial, indexFinal);
          posiciones = posiciones.substring(indexFinal, posiciones.length);
        }

        if (coordenadas !== '') {
          const idSuperficie = superficie.id !== '' ? superficie.id : `ID_${contador}`;
          const nombreSuperficie = superficie.id !== '' ? superficie.id : `nombre_${contador}`;
          poligonos.push({
            modo: 'Exterior',
            fichero: undefined,
            tipo: undefined,
            id: `${idSuperficie}_${contador}`,
            coordenadas,
            nombre: `${nombreSuperficie}_${contador}`,
            sistema: superficie.sistema,
          });

          contador += 1;
        }
      }
    });

    return poligonos;
  }

  getEPSGFromGML32(source, superficies) {
    superficies.map((superficie) => {
      const superficieAux = superficie;
      let indexInicial = superficieAux.surface.indexOf('id=');
      let indexFinal = -2;
      let coordSystem = '';
      if (indexInicial >= 0) {
        indexFinal = superficieAux.surface.indexOf('srsName=', indexInicial + 4);
      }

      if (indexFinal > indexInicial) {
        superficieAux.id = superficieAux.surface.substring(indexInicial + 4, indexFinal - 2);
      }

      indexInicial = superficieAux.surface.indexOf('>', indexFinal + 10);
      if (indexInicial > indexFinal) {
        coordSystem = superficieAux.surface.substring(indexInicial + 25, indexFinal - 1);
      }

      if (coordSystem.indexOf('EPSG::326') > -1) {
        const projectionNumber = parseInt(coordSystem.substring(coordSystem.indexOf('::') + 2, coordSystem.indexOf('>') - 1), 10) - 6800;
        coordSystem = `EPSG::${projectionNumber}`;
      } else if (coordSystem.indexOf('::') === -1) {
        if (coordSystem.indexOf('opengis.net/def/crs')) {
          const projectionType = coordSystem.indexOf('258') === -1 ? coordSystem.indexOf('326') : coordSystem.indexOf('258');
          coordSystem = `EPSG::${coordSystem.substring(projectionType, coordSystem.indexOf('>') - 1)}`;
        } else {
          const gmlGlobalEPSG = source.indexOf('srsName=') + 9;
          coordSystem = source.substring(indexInicial, source.indexOf('"', gmlGlobalEPSG));
        }
      } else {
        coordSystem = coordSystem.substring(coordSystem.indexOf('EPSG'), coordSystem.indexOf('>') - 1);
      }

      superficieAux.sistema = coordSystem.replace('::', ':');
      return superficieAux;
    });

    return superficies;
  }

  /**
  * Converts Openlayers features to Mapea features.
  * @public
  * @function
  * @api
  * @param {Array<OL.Feature>} implFeatures
  * @returns {Array<M.Feature>}
  */
  featuresToFacade(implFeatures) {
    return implFeatures.map((feature) => {
      return M.impl.Feature.olFeature2Facade(feature);
    });
  }
}
